import { Injectable, inject, DestroyRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { MapService } from '@tailormap-viewer/map';
import {
  combineLatest, debounceTime, filter, map, skip, skipWhile, switchMap, withLatestFrom,
} from 'rxjs';
import { selectLoadStatus, selectLayers, selectLayerTreeNodes } from '../../map/state/map.selectors';
import { LoadingStateEnum } from '@tailormap-viewer/shared';
import { BookmarkService } from '../bookmark/bookmark.service';
import { MapBookmarkHelper } from './map-bookmark.helper';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApplicationBookmarkFragments, CompactFilterBookmarkFragment, LayerSortBookmarkFragment, LayerTreeOrderBookmarkFragment,
  LayerSettingsBookmarkFragment, FeatureSelectionBookmarkData,
} from './application-bookmark-fragments';
import { setLayerOpacity, setLayerStyle, setLayerVisibility, updateLayerTreeNodes } from '../../map/state/map.actions';
import { ReadableVisibilityBookmarkHandlerService } from './bookmark-fragment-handlers/readable-visibility-bookmark-handler.service';
import { deepEqual } from 'fast-equals';
import { selectFilterState } from '../../state';
import { addOrUpdateFilterGroups } from '../../state/filter-state/filter.actions';
import { FilterBookmarkHelper } from './filter-bookmark.helper';
import { FilterGroupModel } from '@tailormap-viewer/api';
import { selectAttributeListDataSort } from '../../components/attribute-list/state/attribute-list.selectors';
import { setInitialDataSort } from '../../components/attribute-list/state/attribute-list.actions';
import { SortBookmarkHelper } from './sort-bookmark.helper';
import { FeatureSelectionBookmarkService } from './feature-selection-bookmark.service';
import { FeatureSelectionBookmarkHelper } from './feature-selection-bookmark.helper';
import { selectFeatureInfoShowingBookmarkFeatures } from '../../components/feature-info/state/feature-info.selectors';
import { MobileLayoutBookmarkEnum, MobileLayoutService } from '../viewer-layout/mobile-layout.service';
import { TAILORMAP_CROSS_ORIGIN_API_SERVICE } from '@tailormap-viewer/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ApplicationBookmarkService {
  private destroyRef = inject(DestroyRef);
  private store$ = inject(Store);
  private mapService = inject(MapService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bookmarkService = inject(BookmarkService);
  private readableVisibilityBookmarkHandler = inject(ReadableVisibilityBookmarkHandlerService);
  private featureSelectionBookmarkService = inject(FeatureSelectionBookmarkService);
  private mobileLayoutService = inject(MobileLayoutService);
  private crossOriginApiService = inject(TAILORMAP_CROSS_ORIGIN_API_SERVICE);

  private lastLocationBookmark: string | undefined;
  private lastLayerSettingsBookmark: LayerSettingsBookmarkFragment | undefined;
  private lastOrderingBookmark: LayerTreeOrderBookmarkFragment | undefined;
  private lastSortBookmark: LayerSortBookmarkFragment | undefined;
  private lastFilterBookmark: CompactFilterBookmarkFragment | undefined;
  private lastFeatureSelectionBookmark: string | undefined;

  constructor() {
    let initialRun = true;
    this.route.fragment
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fragment => {
        this.bookmarkService.setBookmark(fragment === null ? undefined : fragment);
        if (initialRun) {
          this.updateBookmarkOnChanges();
          this.readableVisibilityBookmarkHandler.updateBookmarkOnMapChanges();
          this.updateMapOnUrlChanges();
          this.readableVisibilityBookmarkHandler.updateMapOnBookmarkChanges();
        }
        initialRun = false;
      });

    this.crossOriginApiService.getFeatureSelectionMessage$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      event => {
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.FEATURE_SELECTION_BOOKMARK_DESCRIPTOR, event.typedData.value);
      },
    );

  }

  public isEmbeddedApplication$() {
    return this.bookmarkService.registerFragment$(ApplicationBookmarkFragments.EMBED_BOOKMARK_DESCRIPTOR)
      .pipe(map(embedded => embedded === '1'));
  }

  private updateBookmarkOnChanges() {
    this.getLayerSettingsBookmarkData$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.lastLayerSettingsBookmark = bookmark;
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.LAYER_SETTINGS_BOOKMARK_DESCRIPTOR, bookmark);
      });
    this.getOrderBookmarkData$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.lastOrderingBookmark = bookmark;
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.ORDERING_BOOKMARK_DESCRIPTOR, bookmark);
      });
    this.getLocationBookmarkData$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.lastLocationBookmark = bookmark;
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.LOCATION_BOOKMARK_DESCRIPTOR, bookmark);
      });
    this.getFilterBookmarkData$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.lastFilterBookmark = bookmark;
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.FILTER_BOOKMARK_DESCRIPTOR, bookmark);
      });

    this.getSortBookmarkData$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.lastSortBookmark = bookmark;
        this.bookmarkService.updateFragment(ApplicationBookmarkFragments.SORT_BOOKMARK_DESCRIPTOR, bookmark);
      });

    this.getFeaturesRemovedFromFeatureInfo$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((featuresRemoved) => {
        if (featuresRemoved) {
          this.bookmarkService.updateFragment(ApplicationBookmarkFragments.FEATURE_SELECTION_BOOKMARK_DESCRIPTOR, '');
        }
      });

    this.bookmarkService.getBookmarkValue$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bookmark => {
        this.router.navigate([], { relativeTo: this.route, fragment: bookmark, replaceUrl: true });
      });

  }

  private updateMapOnUrlChanges() {
    this.bookmarkService.registerFragment$<string>(ApplicationBookmarkFragments.LOCATION_BOOKMARK_DESCRIPTOR)
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter(locationBookmark => locationBookmark !== this.lastLocationBookmark),
      )
      .subscribe(locationBookmark => {
        const bookmark = MapBookmarkHelper.locationAndZoomFromFragment(locationBookmark || '');
        if (bookmark) {
          this.mapService.setCenterAndZoom(bookmark[0], bookmark[1]);
        }
      });

    this.bookmarkService.registerFragment$<LayerSettingsBookmarkFragment>(ApplicationBookmarkFragments.LAYER_SETTINGS_BOOKMARK_DESCRIPTOR)
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter(layerBookmark => !deepEqual(this.lastLayerSettingsBookmark, layerBookmark)),
        withLatestFrom(this.store$.select(selectLayers)),
      )
      .subscribe(([ layerBookmark, extendedAppLayers ]) => {
        if (layerBookmark === null) {
          return;
        }
        const layerSettingsChanges = MapBookmarkHelper.layerSettingsFromFragment(layerBookmark, extendedAppLayers, false);
        if (layerSettingsChanges.visibilityChanges.length > 0) {
          this.store$.dispatch(setLayerVisibility({ visibility: layerSettingsChanges.visibilityChanges }));
        }
        if (layerSettingsChanges.opacityChanges.length > 0) {
          this.store$.dispatch(setLayerOpacity({ opacity: layerSettingsChanges.opacityChanges }));
        }
        if (layerSettingsChanges.styleChanges.length > 0) {
          this.store$.dispatch(setLayerStyle({ style: layerSettingsChanges.styleChanges }));
        }
      });

    this.bookmarkService.registerFragment$<LayerTreeOrderBookmarkFragment>(ApplicationBookmarkFragments.ORDERING_BOOKMARK_DESCRIPTOR)
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter(orderBookmark => !deepEqual(this.lastOrderingBookmark, orderBookmark)),
        withLatestFrom(this.store$.select(selectLayerTreeNodes)),
      )
      .subscribe(([ orderBookmark, layerTreeNodes ]) => {
        const bookmarkLayerOrder = MapBookmarkHelper.layerTreeOrderFromFragment(orderBookmark, layerTreeNodes);
        const updatedLayerTreeNodes = layerTreeNodes.map(node => {
          const matches = bookmarkLayerOrder.find(a => a.nodeId === node.id);
          if (matches !== undefined) {
            return {
              ...node,
              childrenIds: matches.children,
            };
          }
          return node;
        });
        this.store$.dispatch(updateLayerTreeNodes({ layerTreeNodes: updatedLayerTreeNodes }));
      });

    this.bookmarkService.registerFragment$<LayerSortBookmarkFragment>(ApplicationBookmarkFragments.SORT_BOOKMARK_DESCRIPTOR)
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter(sortBookmark => !deepEqual(this.lastSortBookmark, sortBookmark)),
    ).subscribe(sortBookmark => {
      this.store$.dispatch(setInitialDataSort({ initialDataSort: SortBookmarkHelper.initialSortDataFromFragment(sortBookmark ?? []) }));
    });

    this.store$.select(selectLoadStatus).pipe(
      skip(1),
      takeUntilDestroyed(this.destroyRef),
      filter(loadStatus => loadStatus === LoadingStateEnum.LOADED),
      switchMap(() => this.bookmarkService.registerFragment$<CompactFilterBookmarkFragment>(ApplicationBookmarkFragments.FILTER_BOOKMARK_DESCRIPTOR)),
      filter(filterBookmark => !deepEqual(this.lastFilterBookmark, filterBookmark)),
      withLatestFrom(this.store$.select(selectFilterState)),
    ).subscribe(([ filterBookmark, filterState ]) => {
        const currentFilterGroupIds = filterState.currentFilterGroups.map(fg => fg.id);
        const newAndUpdatedFilterGroups: FilterGroupModel[] = [];

        filterBookmark?.a?.filter(bfg => !currentFilterGroupIds.includes(bfg.id)).forEach(bfg => {
          const filterGroup = FilterBookmarkHelper.attributeFilterGroupFromBookmark(bfg);
          newAndUpdatedFilterGroups.push(filterGroup);
        });

        filterBookmark?.s?.filter(bfg => !currentFilterGroupIds.includes(bfg.id)).forEach(bfg => {
          const filterGroup = FilterBookmarkHelper.spatialFilterGroupFromBookmark(bfg);
          newAndUpdatedFilterGroups.push(filterGroup);
        });

        filterBookmark?.p?.forEach(bfg => {
          const filterGroup = FilterBookmarkHelper.presetFilterGroupFromBookmark(filterState, bfg);
          if (filterGroup) {
            newAndUpdatedFilterGroups.push(filterGroup);
          }
        });

        if (newAndUpdatedFilterGroups.length > 0) {
          this.store$.dispatch(addOrUpdateFilterGroups({ filterGroups: newAndUpdatedFilterGroups }));
        }
      });

    this.store$.select(selectLoadStatus).pipe(
      skip(1),
      takeUntilDestroyed(this.destroyRef),
      filter(loadStatus => loadStatus === LoadingStateEnum.LOADED),
      switchMap(() => this.bookmarkService.registerFragment$<string>(ApplicationBookmarkFragments.FEATURE_SELECTION_BOOKMARK_DESCRIPTOR)),
      filter(featureSelectionFragment => featureSelectionFragment !== this.lastFeatureSelectionBookmark),
      withLatestFrom(this.isEmbeddedApplication$()),
    ).subscribe(([ featureSelectionFragmentString, isEmbedded ]) => {
        this.lastFeatureSelectionBookmark = featureSelectionFragmentString || undefined;
        const featureSelectionFragment: FeatureSelectionBookmarkData | null = FeatureSelectionBookmarkHelper.getFragmentFromBookmark(featureSelectionFragmentString || null);
        this.featureSelectionBookmarkService.clearSelection();
        this.featureSelectionBookmarkService.applyBookmarkFragment(featureSelectionFragment, isEmbedded);
      });

    this.bookmarkService.registerFragment$<string>(ApplicationBookmarkFragments.MOBILE_LAYOUT_BOOKMARK_DESCRIPTOR)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(mobile => {
        if (mobile === '1') {
          this.mobileLayoutService.setMobileLayoutBookmark(MobileLayoutBookmarkEnum.ENABLED);
        } else if (mobile === '0') {
          this.mobileLayoutService.setMobileLayoutBookmark(MobileLayoutBookmarkEnum.DISABLED);
        } else {
          this.mobileLayoutService.setMobileLayoutBookmark(MobileLayoutBookmarkEnum.AUTO);
        }
      });
  }

  private getLayerSettingsBookmarkData$() {
    return combineLatest([
      this.store$.select(selectLayers),
      this.store$.select(selectLoadStatus),
    ]).pipe(
      filter(([ , loadStatus ]) => loadStatus === LoadingStateEnum.LOADED),
      map(([layers]) => {
        return MapBookmarkHelper.fragmentFromLayerSettings(layers);
      }),
    );
  }

  private getOrderBookmarkData$() {
    return combineLatest([
      this.store$.select(selectLayerTreeNodes),
      this.store$.select(selectLoadStatus),
    ]).pipe(
      filter(([ , loadStatus ]) => loadStatus === LoadingStateEnum.LOADED),
      map(([layers]) => {
        return MapBookmarkHelper.fragmentFromLayerTreeOrder(layers);
      }),
    );
  }

  private getLocationBookmarkData$() {
    return combineLatest([ this.mapService.getMapViewDetails$(), this.mapService.getUnitsOfMeasure$() ])
      .pipe(
        debounceTime(250),
        map(([ info, measure ]) => MapBookmarkHelper.fragmentFromLocationAndZoom(info, measure)),
        filter((fragment): fragment is string => fragment !== undefined),
      );
  }

  private getFilterBookmarkData$() {
    return combineLatest([
      this.store$.select(selectFilterState),
      this.store$.select(selectLoadStatus),
    ]).pipe(
      filter(([ , loadStatus ]) => loadStatus === LoadingStateEnum.LOADED),
      takeUntilDestroyed(this.destroyRef),
      debounceTime(250),
      map(([filterState]) => FilterBookmarkHelper.fragmentFromFilterState(filterState)),
    );
  }

  private getSortBookmarkData$() {
    return combineLatest([
      this.store$.select(selectAttributeListDataSort),
      this.store$.select(selectLoadStatus),
    ]).pipe(
      filter(([ , loadStatus ]) => loadStatus === LoadingStateEnum.LOADED),
      takeUntilDestroyed(this.destroyRef),
      debounceTime(250),
      map(([sortState]) => SortBookmarkHelper.fragmentFromSortState(sortState)),
    );
  }

  /*
  Emit true after features from bookmark have been shown and then removed from feature info
   */
  private getFeaturesRemovedFromFeatureInfo$() {
    return this.store$.select(selectFeatureInfoShowingBookmarkFeatures)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(showingBookmarkFeatures => !showingBookmarkFeatures),
        map(showingBookmarkFeatures => !showingBookmarkFeatures),
      );
  }
}
